import { FC, ReactElement, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Collapse,
  IconButton,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";

import { tokens } from "@/tokens";
import { tableCompactSx } from "@/components/table/table-styles";
import TablePagination from "@/components/paginations/table-pagination";
import { tariff_code_placeholder } from "@/constants";
import { numberWithCommas } from "@/utils";

interface GroupedByManufacturerProps {
  manufacturers: PrefOriginManufacturer[];
  opportunities: PrefOriginOpportunity[];
  isLoading: boolean;
  onOpportunityClick: (row: PrefOriginOpportunity, manufacturerId?: string) => void;
}

const statusConfig: Record<string, { label: string; color: string; bg: string }> = {
  FULLY_CLAIMED: { label: "Fully Claimed", color: "#2E7D32", bg: "#E8F5E9" },
  PARTIALLY_CLAIMED: { label: "Partial", color: "#ED6C02", bg: "#FFF3E0" },
  NOT_CLAIMED: { label: "Not Claimed", color: "#D32F2F", bg: "#FFEBEE" },
};

const getInitials = (name: string) => {
  const parts = name.split(/[\s\-]+/);
  return parts.length > 1
    ? (parts[0][0] + parts[1][0]).toUpperCase()
    : name.slice(0, 2).toUpperCase();
};

const GroupedByManufacturer: FC<GroupedByManufacturerProps> = ({
  manufacturers,
  opportunities,
  isLoading,
  onOpportunityClick,
}): ReactElement => {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [mfrPage, setMfrPage] = useState(1);
  const MFR_PAGE_SIZE = 10;

  const toggle = (mid: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(mid)) next.delete(mid);
      else next.add(mid);
      return next;
    });
  };

  const getOpportunitiesForMfr = (mid: string) =>
    opportunities.filter((o) => o.manufacturers?.includes(mid));

  return (
    <Box
      sx={{
        borderRadius: tokens.radius.lg,
        border: `1px solid ${tokens.border.default}`,
        bgcolor: tokens.bg.surface,
        overflow: "hidden",
        boxShadow: tokens.shadow.sm,
        ...tableCompactSx,
      }}
    >
      <TableContainer sx={{ flex: 1, overflow: "auto" }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 48 }} />
              <TableCell>Manufacturer</TableCell>
              <TableCell align="center">Opportunities</TableCell>
              <TableCell align="center">Entries</TableCell>
              <TableCell align="right">Potential Savings</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 8 }).map((_, i) => (
                <TableRow key={`skeleton-${i}`}>
                  {Array.from({ length: 5 }).map((_, j) => (
                    <TableCell key={j}>
                      <Skeleton variant="rounded" height={18} />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : manufacturers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} sx={{ border: "none" }}>
                  <Stack alignItems="center" justifyContent="center" spacing={2} sx={{ py: 6 }}>
                    <img src={tariff_code_placeholder} alt="No data" style={{ width: 120, height: 120, opacity: 0.6 }} />
                    <Typography variant="h6" fontWeight={tokens.fontWeight.semibold} color={tokens.text.primary}>
                      No Manufacturers Found
                    </Typography>
                    <Typography variant="body2" sx={{ color: tokens.text.secondary, maxWidth: 420, textAlign: "center", lineHeight: 1.6 }}>
                      No manufacturers with FTA-eligible entries found for the selected date range.
                    </Typography>
                  </Stack>
                </TableCell>
              </TableRow>
            ) : (
              (() => {
                const filtered = manufacturers.filter((mfr) => getOpportunitiesForMfr(mfr.manufacturerID).length > 0);
                return filtered.slice((mfrPage - 1) * MFR_PAGE_SIZE, mfrPage * MFR_PAGE_SIZE);
              })().map((mfr) => {
                const isOpen = expanded.has(mfr.manufacturerID);
                const mfrOpps = getOpportunitiesForMfr(mfr.manufacturerID);
                const totalSavings = mfrOpps.reduce((sum, o) => sum + (o.potentialSavings || 0), 0);

                return (
                  <ManufacturerRow
                    key={mfr.manufacturerID}
                    manufacturer={mfr}
                    opportunities={mfrOpps}
                    totalSavings={totalSavings}
                    isExpanded={isOpen}
                    onToggle={() => toggle(mfr.manufacturerID)}
                    onOpportunityClick={onOpportunityClick}
                  />
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Manufacturer-level pagination */}
      {(() => {
        const filtered = manufacturers.filter((mfr) => getOpportunitiesForMfr(mfr.manufacturerID).length > 0);
        const totalMfrs = filtered.length;
        if (totalMfrs <= MFR_PAGE_SIZE) return null;
        return (
          <Box borderTop={`1px solid ${tokens.border.light}`}>
            <TablePagination
              limit={MFR_PAGE_SIZE}
              page={mfrPage}
              total={totalMfrs}
              handlePageChange={setMfrPage}
            />
          </Box>
        );
      })()}
    </Box>
  );
};

// --- Manufacturer row with nested opportunities ---

interface ManufacturerRowProps {
  manufacturer: PrefOriginManufacturer;
  opportunities: PrefOriginOpportunity[];
  totalSavings: number;
  isExpanded: boolean;
  onToggle: () => void;
  onOpportunityClick: (row: PrefOriginOpportunity, manufacturerId?: string) => void;
}

const NESTED_PAGE_SIZE = 5;

const ManufacturerRow: FC<ManufacturerRowProps> = ({
  manufacturer,
  opportunities,
  totalSavings,
  isExpanded,
  onToggle,
  onOpportunityClick,
}) => {
  const displayName = manufacturer.manufacturerName || manufacturer.manufacturerID;
  const [nestedPage, setNestedPage] = useState(1);
  const nestedTotal = opportunities.length;
  const pagedOpportunities = opportunities.slice(
    (nestedPage - 1) * NESTED_PAGE_SIZE,
    nestedPage * NESTED_PAGE_SIZE
  );

  return (
    <>
      {/* Manufacturer summary row */}
      <TableRow
        onClick={onToggle}
        sx={{
          cursor: "pointer",
          backgroundColor: isExpanded ? tokens.bg.hover : tokens.bg.surface,
          transition: "background-color 150ms ease",
          "&:hover": { backgroundColor: tokens.bg.hover },
          "& > td": {
            borderBottom: isExpanded ? "none" : `1px solid ${tokens.border.light}`,
          },
        }}
      >
        <TableCell sx={{ width: 48, p: 1, pl: 1.5 }}>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); onToggle(); }} sx={{ p: 0.5 }}>
            {isExpanded ? (
              <KeyboardArrowDown sx={{ color: tokens.brand.primary, fontSize: 20 }} />
            ) : (
              <KeyboardArrowRight sx={{ color: tokens.text.secondary, fontSize: 20 }} />
            )}
          </IconButton>
        </TableCell>
        <TableCell>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Avatar
              sx={{
                width: 32,
                height: 32,
                bgcolor: tokens.bg.hover,
                color: tokens.brand.primary,
                fontSize: tokens.fontSize.base,
                fontWeight: tokens.fontWeight.bold,
                borderRadius: tokens.radius.sm,
              }}
            >
              {getInitials(displayName)}
            </Avatar>
            <Stack>
              <Typography variant="body2" fontWeight={tokens.fontWeight.bold} sx={{ fontSize: tokens.fontSize.base, color: tokens.text.primary }}>
                {displayName}
              </Typography>
              <Typography variant="caption" sx={{ color: tokens.text.muted, fontFamily: "monospace" }}>
                {manufacturer.manufacturerID}
              </Typography>
            </Stack>
          </Stack>
        </TableCell>
        <TableCell align="center">
          <Typography variant="body2" sx={{ fontSize: tokens.fontSize.base, color: tokens.text.primary }}>
            {opportunities.length}
          </Typography>
        </TableCell>
        <TableCell align="center">
          <Typography variant="body2" sx={{ fontSize: tokens.fontSize.base, color: tokens.text.primary }}>
            {numberWithCommas(manufacturer.entryCount)}
          </Typography>
        </TableCell>
        <TableCell align="right">
          <Typography
            variant="body2"
            sx={{
              fontSize: tokens.fontSize.base,
              fontWeight: tokens.fontWeight.bold,
              color: totalSavings > 0 ? "#2E7D32" : tokens.text.primary,
            }}
          >
            ${numberWithCommas(totalSavings)}
          </Typography>
        </TableCell>
      </TableRow>

      {/* Expanded: nested opportunities */}
      <TableRow sx={{ ...(!isExpanded && { display: "none" }) }}>
        <TableCell colSpan={5} sx={{ p: 0, border: "none" }}>
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <Box sx={{ backgroundColor: tokens.bg.hover, px: 2, py: 1.5 }}>
              {/* Action bar */}
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="flex-end"
                sx={{ mb: 1.5 }}
              >
                <Button
                  variant="contained"
                  size="small"
                  disabled
                  startIcon={<DescriptionOutlinedIcon sx={{ fontSize: 16 }} />}
                  sx={{
                    textTransform: "none",
                    fontWeight: tokens.fontWeight.semibold,
                    fontSize: tokens.fontSize.sm2,
                    opacity: 0.6,
                  }}
                >
                  Create Certificate of Origin (Coming Soon)
                </Button>
              </Stack>

              <Table size="small">
                <TableHead>
                  <TableRow>
                    {["HTS Code", "Country", "Program", "Status", "Entries", "Claimed / Unclaimed", "Goods Value", "Savings"].map((h) => (
                      <TableCell
                        key={h}
                        sx={{
                          fontSize: tokens.fontSize["2xs"],
                          fontWeight: tokens.fontWeight.semibold,
                          color: tokens.text.muted,
                          textTransform: "uppercase",
                          letterSpacing: "0.05em",
                          borderBottom: `1px solid ${tokens.border.light}`,
                          py: 0.75,
                          px: 1.5,
                        }}
                      >
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagedOpportunities.map((opp, idx) => {
                    const statusCfg = statusConfig[opp.status] ?? {
                      label: opp.status,
                      color: tokens.text.secondary,
                      bg: tokens.bg.hover,
                    };
                    return (
                      <TableRow
                        key={`${opp.htsNumberFull}-${opp.countryOfOriginCode}-${idx}`}
                        onClick={() => onOpportunityClick(opp, manufacturer.manufacturerID)}
                        sx={{
                          cursor: "pointer",
                          backgroundColor: tokens.bg.surface,
                          "&:hover": { backgroundColor: "#fff" },
                          "& > td": { borderBottom: `1px solid ${tokens.border.light}`, py: 1, px: 1.5 },
                        }}
                      >
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: "monospace", fontWeight: tokens.fontWeight.bold, fontSize: tokens.fontSize.sm2, color: tokens.text.primary }}>
                            {opp.htsNumberFull}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontSize: tokens.fontSize.sm2, color: tokens.text.primary }}>
                            {opp.countryName || opp.countryOfOriginCode}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontSize: tokens.fontSize.sm2, color: tokens.text.primary, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 180 }}>
                            {opp.program}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box
                            component="span"
                            sx={{
                              display: "inline-block",
                              px: 0.75,
                              py: 0.25,
                              borderRadius: "4px",
                              fontSize: tokens.fontSize["2xs"],
                              fontWeight: tokens.fontWeight.semibold,
                              color: statusCfg.color,
                              backgroundColor: statusCfg.bg,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {statusCfg.label}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontSize: tokens.fontSize.sm2, color: tokens.text.primary }}>
                            {numberWithCommas(opp.totalEntries)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontSize: tokens.fontSize.sm2, color: tokens.text.primary }}>
                            {numberWithCommas(opp.claimedEntries)} / {numberWithCommas(opp.unclaimedEntries)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontSize: tokens.fontSize.sm2, color: tokens.text.primary }}>
                            ${numberWithCommas(opp.goodsValue)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: tokens.fontSize.sm2,
                              fontWeight: tokens.fontWeight.bold,
                              color: opp.potentialSavings > 0 ? "#2E7D32" : tokens.text.primary,
                            }}
                          >
                            ${numberWithCommas(opp.potentialSavings)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>

              {/* Nested pagination */}
              {nestedTotal > NESTED_PAGE_SIZE && (
                <Box sx={{ mt: 1 }}>
                  <TablePagination
                    limit={NESTED_PAGE_SIZE}
                    page={nestedPage}
                    total={nestedTotal}
                    handlePageChange={setNestedPage}
                  />
                </Box>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

export default GroupedByManufacturer;
